import { useState, useEffect } from 'react';

// Standard breakpoints (Bootstrap-inspired)
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 831,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

type BreakpointKey = keyof typeof breakpoints;

/**
 * Hook that returns whether the viewport is smaller than the specified breakpoint
 */
const useResponsive = (query: BreakpointKey = 'md') => {
  const breakpoint = breakpoints[query];
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // Set initial value on mount
    setMatches(window.innerWidth < breakpoint);

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handleChange = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Add listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange); // For older browsers
    }

    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [breakpoint]);

  return matches;
};

// Range check (between min and max breakpoints)
export const useResponsiveRange = (min: BreakpointKey, max: BreakpointKey) => {
  const minBreakpoint = breakpoints[min];
  const maxBreakpoint = breakpoints[max];
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // Set initial value on mount
    const width = window.innerWidth;
    setMatches(width >= minBreakpoint && width < maxBreakpoint);

    const checkRange = () => {
      const width = window.innerWidth;
      setMatches(width >= minBreakpoint && width < maxBreakpoint);
    };

    window.addEventListener('resize', checkRange);

    // Clean up
    return () => {
      window.removeEventListener('resize', checkRange);
    };
  }, [minBreakpoint, maxBreakpoint]);

  return matches;
};

export default useResponsive;
